// usePublicIp — singleton-style hook wrapping detectPublicIp().
//
// F10.2 needs a stable view of "what's my public IPv4 as STUN sees it?"
// available across components without each one spawning its own
// RTCPeerConnection. detectPublicIp() already caches the result at the
// module level; this hook subscribes to that cache and surfaces it as
// React state.
//
// Lifecycle:
//   - First mount triggers detectPublicIp(); subsequent mounts read the
//     cached value synchronously (status === 'ready' on first render).
//   - On `online` event we reset the cache (publicIp.js handles this) and
//     re-run detection. Status flips back to 'pending' until the new IP
//     resolves.
//   - STUN failure / IPv6-only network → ip === null, status 'unavailable'.

import { useEffect, useState } from 'react';
import { detectPublicIp, resetPublicIpCache } from '../lib/p2p/publicIp.js';

/**
 * @typedef {{ ip: string | null, status: 'pending' | 'ready' | 'unavailable' }} PublicIpState
 */

// Module-level subscription set so the `online` listener and the resolver
// can fan results out to every mounted consumer with a single detect call.
const subscribers = new Set();
let cachedState = { ip: null, status: 'pending' };
let inflight = false;

function notifyAll(next) {
  cachedState = next;
  for (const cb of subscribers) {
    try {
      cb(next);
    } catch {
      /* one bad consumer shouldn't break the broadcast */
    }
  }
}

function runDetect() {
  if (inflight) return;
  inflight = true;
  // Push pending state up so the UI can show a scanning indicator.
  notifyAll({ ip: null, status: 'pending' });
  detectPublicIp()
    .then((ip) => {
      notifyAll({
        ip: typeof ip === 'string' && ip ? ip : null,
        status: typeof ip === 'string' && ip ? 'ready' : 'unavailable',
      });
    })
    .catch(() => {
      notifyAll({ ip: null, status: 'unavailable' });
    })
    .finally(() => {
      inflight = false;
    });
}

// Re-detect when the browser thinks we're back online. The publicIp.js cache
// also resets on this event independently — we duplicate the listener here so
// any mounted consumer wakes up. Guarded for non-browser test envs.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('online', () => {
    resetPublicIpCache();
    if (subscribers.size > 0) runDetect();
  });
}

/**
 * @returns {PublicIpState}
 */
export function usePublicIp() {
  const [state, setState] = useState(cachedState);

  useEffect(() => {
    subscribers.add(setState);
    // Sync to whatever the cache holds now.
    setState(cachedState);
    if (cachedState.status === 'pending') runDetect();
    return () => {
      subscribers.delete(setState);
    };
  }, []);

  return state;
}

// Test-only — reset module state between specs without monkey-patching.
export function __resetForTests() {
  subscribers.clear();
  cachedState = { ip: null, status: 'pending' };
  inflight = false;
  resetPublicIpCache();
}

export default usePublicIp;
